import { Alert, DateRange, VFlow } from 'bold-ui'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContent } from 'components/layout/PageContent'
import { PageHeaderSection } from 'components/PageHeaderSection'
import { PivotTable, PivotTableInitialValues, PivotTableProps } from 'components/pivot-table/PivotTable'
import { useTreeBuilder } from 'components/pivot-table/tree-builder/useTreeBuilder'
import { useAbsenteismosLazyQuery } from 'graphql/hooks.generated'
import { TipoModuloEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import React, { useCallback, useEffect, useState } from 'react'
import { isUndefinedOrNull } from 'util/checks'

import {
  NO_ASYNC_LOADING_MESSAGE,
  RELATORIOS_GERENCIAIS_ANALYTICS_SELECIONOU_RELATORIO_EVENTO,
} from '../common/model-relatorioGerencial'
import { RelatorioGerencialFiltrosFormModel } from '../components/form/model-relatorioGerencialFiltrosForm'
import { RelatorioGerencialTableWrapper } from '../components/RelatorioGerencialTableWrapper'
import { convertToRelatorioGerencialAbsenteismoInput } from './converter-relatorioGerencialAbsenteismo'
import { absenteismoKeyMapping } from './keyMapping-relatorioGerencialAbsenteismo'
import { RelatorioGerencialAbsenteismoModel } from './model-relatorioGerencialAbsenteismo'

const NAME = 'Relatório gerencial de absenteísmo'

export function RelatorioGerencialAbsenteismoView() {
  const [getAbsenteismos, { loading, data }] = useAbsenteismosLazyQuery({
    fetchPolicy: 'cache-first',
  })
  const [pivotTableInitialValues, setPivotTableInitialValues] = useState<
    PivotTableInitialValues<RelatorioGerencialAbsenteismoModel>
  >()

  const { analytics } = useFirebase()

  const [dateRangeFilter, setDateRangeFilter] = useState<DateRange>()

  const handleSubmit = useCallback(
    (filtros: RelatorioGerencialFiltrosFormModel) => {
      setDateRangeFilter(filtros.periodo)
      getAbsenteismos({
        variables: {
          input: convertToRelatorioGerencialAbsenteismoInput(filtros),
        },
      })
    },
    [getAbsenteismos]
  )

  const isDataNotUndefinedOrNull = !isUndefinedOrNull(data?.absenteismos)
  const isEmpty = isDataNotUndefinedOrNull && data?.absenteismos.isEmpty()

  useEffect(() => {
    analytics.logEvent(RELATORIOS_GERENCIAIS_ANALYTICS_SELECIONOU_RELATORIO_EVENTO, {
      relatorio: NAME,
    })
  }, [analytics])

  return (
    <>
      <Breadcrumb title={NAME} />
      <PageHeaderSection title={NAME} />
      <PageContent type='filled'>
        <VFlow>
          <Alert inline type='info'>
            O relatório de absenteísmo apresenta a contagem dos agendamentos em que o cidadão não compareceu ou não
            aguardou.
          </Alert>

          <RelatorioGerencialTableWrapper
            loading={loading}
            isEmpty={isEmpty}
            onSubmit={handleSubmit}
            modulo={TipoModuloEnum.RELATORIO_GERENCIAL_ABSENTEISMO}
            keyMapping={absenteismoKeyMapping}
            loadingMessage={NO_ASYNC_LOADING_MESSAGE}
            setPivotTableInitialValues={setPivotTableInitialValues}
          >
            {isDataNotUndefinedOrNull && (
              <AbsenteismoTable
                data={data.absenteismos}
                dateRangeFilter={dateRangeFilter}
                initialValues={pivotTableInitialValues}
              />
            )}
          </RelatorioGerencialTableWrapper>
        </VFlow>
      </PageContent>
    </>
  )
}

interface AbsenteismoTableProps extends Pick<PivotTableProps<RelatorioGerencialAbsenteismoModel>, 'initialValues'> {
  data: RelatorioGerencialAbsenteismoModel[]
  dateRangeFilter: DateRange
}

function AbsenteismoTable(props: AbsenteismoTableProps) {
  const { data, dateRangeFilter, initialValues } = props

  const {
    metaState,
    treeBuilderStatus: { isBuilding },
    ...rest
  } = useTreeBuilder<RelatorioGerencialAbsenteismoModel>({
    keyMapping: absenteismoKeyMapping,
    data: data,
  })

  if (metaState?.keyValues && metaState?.numberKeys) {
    return (
      <PivotTable<RelatorioGerencialAbsenteismoModel>
        treeMeta={metaState}
        keyMapping={absenteismoKeyMapping}
        title={NAME}
        dateRangeFilter={dateRangeFilter}
        isBuilding={isBuilding}
        initialValues={initialValues}
        {...rest}
      />
    )
  } else {
    return null
  }
}
